<template>
  <Component
    :is="selectedView"
    :count="count"
    :query="query"
    :fetchingStatus="fetchingStatus"
    @update:count="$emit('update:count', $event)"
    @update:fetchingStatus="emit('update:fetchingStatus', $event)"
  >
    <template v-slot:state v-if="count !== null || fetchingStatus === 'error'">
      <State
        :type="state.type"
        :title="state.title"
        :text="state.text"
        :button="state.button"
        :disabled="state.disabled"
        :action="state.action"
        @click:button="handleStateEmit(state.emit)"
      />
    </template>
  </Component>
</template>

<script setup>
// Import Vue Utilites
import { computed, inject } from "vue";

// Import Utilities
import { useI18n } from "vue-i18n";
import { useFilters } from "@/Composables/useFilters";
import { storeToRefs } from "pinia";

// Import Components
import SpacesHierarchy from "./SpacesHierarchy.vue";
import { State } from "@/SharedComponents/State";

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Emits
const emit = defineEmits([
  "update:count",
  "update:fetchingStatus",
  "add:space",
  "reset",
  "reload",
]);

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  query: { type: String, required: true },
  fetchingStatus: { type: String, required: true },
});

// Computed props
const selectedView = computed(() => {
  return SpacesHierarchy;
});

const state = computed(() => {
  let type = "Spaces";
  let title = t("components.state.no_spaces.title");
  let text = t("components.state.no_spaces.text");
  let button = t("components.state.no_spaces.button");
  let disabled = !hasPermission(permissions.createSpace);
  let emit = "add:space";
  let action = "add-space";

  if (props.query) {
    type = "NoResults";
    title = t("components.state.no_results.title");
    text = t("components.state.no_results.text");
    button = t("components.state.no_results.button");
    disabled = false;
    emit = "reset";
    action = "reset";
  }

  if (props.fetchingStatus === "error") {
    type = "Error";
    title = t("components.state.error.title");
    text = t("components.state.error.text");
    button = t("components.state.error.button");
    disabled = false;
    emit = "reload";
    action = "reload";
  }

  if (!globalFilterValues.value.tenant) {
    type = "Spaces";
    title = t("components.state.no_spaces_building.title");
    text = t("components.state.no_spaces_building.text");
    button = false;
    disabled = false;
    emit = "";
    action = "";
  }

  return {
    type: type,
    title: title,
    text: text,
    button: button,
    disabled: disabled,
    emit: emit,
    action: action,
  };
});

// Functions
const handleStateEmit = (value) => {
  emit(value);
};
</script>
